/**
 * The fixed list of CPM campaign categories, in canonical order. Mirrors the
 * backend's src/lib/cpmCampaignCategories.js; both repos pin it with the
 * byte-identical fixture __tests__/fixtures/cpmCampaignCategories.json.
 * Adding a category is a two-repo change (plus its label in both locales).
 */
export const CAMPAIGN_CATEGORIES = [
  'business',
  'marketing',
  'finance',
  'personal_development',
  'health',
  'news',
  'sports',
  'entertainment',
  'technology',
  'education',
  'products',
  'podcast',
] as const;

export type CampaignCategory = (typeof CAMPAIGN_CATEGORIES)[number];

export const MAX_CAMPAIGN_CATEGORIES = 3;

export function isCampaignCategory(value: unknown): value is CampaignCategory {
  return typeof value === 'string' && (CAMPAIGN_CATEGORIES as readonly string[]).includes(value);
}

/** What a card may render: known keys only, canonical order, at most three. */
export function visibleCampaignCategories(
  input?: ReadonlyArray<string | null | undefined> | null,
): CampaignCategory[] {
  const picked = new Set((input ?? []).filter(isCampaignCategory));
  return CAMPAIGN_CATEGORIES.filter((key) => picked.has(key)).slice(0, MAX_CAMPAIGN_CATEGORIES);
}

/** Admin toggle: removes a picked key, adds an unpicked one unless three are picked. */
export function toggleCampaignCategory(
  current: ReadonlyArray<string>,
  key: CampaignCategory,
): CampaignCategory[] {
  const visible = visibleCampaignCategories(current);
  if (visible.includes(key)) return visible.filter((k) => k !== key);
  if (visible.length >= MAX_CAMPAIGN_CATEGORIES) return visible;
  return visibleCampaignCategories([...visible, key]);
}
